import clsx from 'clsx';
import React, {FC, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useLocation} from 'react-router-dom';

import CooperationModal from 'components/CooperationModal';
import {useBooleanState, useWindowDimensions} from 'hooks';
import {ROUTES, URLS} from 'constants/routes';

import logoImg from 'assets/v3/logo.png';

import {LanguageSwitcher} from './LanguageSwitcher';
import NavDropdown from './NavDropdown';
import {HamburgerButton, MobileMenuPanel} from './MobileMenu';
import {setCooperationOpener, useNavGroups, useTopNavLinks} from './navData';
import useCurrentUser from './useCurrentUser';

/**
 * 新版玻璃态导航（v3），替代原 TopNavDesktopItems/TopNavMobileMenu 系列组件。
 * 结构照抄设计稿 docs/designs/xiangji_home_v3/index.html 的导航区块。
 *
 * 布局要点：
 * - 外层 div.v3-scope 是纯作用域容器（selector 策略下 .v3-scope 自身不吃工具类），
 *   fixed/z-50 等一律落在内层 nav 上。
 * - 导航为 fixed 定位（设计稿如此），非首页内容区靠 .v3-page-offset 补顶距（见 Layout）。
 * - CooperationModal 经 createPortal 渲染到 document.body，不受 .v3-scope reset 影响。
 * - 移动端抽屉渲染在 nav 之外（glass-nav 的 backdrop-filter 会限制 nav 内 fixed 后代）。
 */

interface ComponentProps {
  className?: string;
}

const TopNav: FC<ComponentProps> = ({className}) => {
  const [mobileMenuIsOpen, toggleMobileMenu, , closeMobileMenu] = useBooleanState(false);
  const [isCooperationOpen, setIsCooperationOpen] = useState(false);
  const {pathname, search} = useLocation();
  const {width} = useWindowDimensions();
  const {t} = useTranslation('common');
  const navGroups = useNavGroups();
  const topNavLinks = useTopNavLinks();
  // 登录态与昵称分离：已登录但未设置昵称（name 空）时不再误显示「登录」
  const {isLoggedIn, name: currentUserName} = useCurrentUser();

  // 路由变化时收起移动端抽屉
  useEffect(() => {
    closeMobileMenu();
  }, [closeMobileMenu, pathname]);

  // 进入桌面断点时收起
  useEffect(() => {
    if (width > 1024) closeMobileMenu();
  }, [closeMobileMenu, width]);

  useEffect(() => {
    setCooperationOpener(() => setIsCooperationOpen(true));
  }, []);

  /** 沿用原 TopNavDesktopItems 的登录跳转：register_invite 场景走邀请链接 */
  const loginUrl = search && search.includes('register_invite') ? `${URLS.registerInviteUrl}${search}` : URLS.login;

  return (
    <div className="v3-scope">
      <CooperationModal isOpen={isCooperationOpen} onClose={() => setIsCooperationOpen(false)} />
      <nav className={clsx('glass-nav fixed top-0 left-0 right-0 z-50', className)}>
        <div className="max-w-[90rem] mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <div className="flex items-center">
              <Link aria-label={t('nav.homeAria')} className="flex items-center gap-2.5" to={ROUTES.home}>
                <img
                  alt="象寄翻译 Xiangji AI"
                  className="h-9 w-auto"
                  decoding="async"
                  height={210}
                  src={logoImg}
                  width={700}
                />
              </Link>
            </div>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-8">
              {navGroups.map((group, index) => (
                <React.Fragment key={group.label}>
                  <NavDropdown group={group} />
                  {/* 浏览器插件/API对接：非下拉顶层链接，紧跟在「产品服务」（首个分组）之后 */}
                  {index === 0 &&
                    topNavLinks.map((link) =>
                      link.to ? (
                        <Link
                          className="nav-link text-sm text-slate-600 hover:text-slate-900 font-medium py-2"
                          key={link.title}
                          to={link.to}
                        >
                          {link.title}
                        </Link>
                      ) : (
                        <a
                          className="nav-link text-sm text-slate-600 hover:text-slate-900 font-medium py-2"
                          href={link.href}
                          key={link.title}
                          rel="noreferrer"
                          target="_blank"
                        >
                          {link.title}
                        </a>
                      ),
                    )}
                </React.Fragment>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              {/* 问候语仅在已登录且有昵称时展示；已登录但无昵称时不显示文字链接（主按钮已是「进入工作台」） */}
              {currentUserName ? (
                <a
                  className="hidden sm:inline-flex text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors"
                  href={loginUrl}
                >
                  {t('nav.cta.greeting', {name: currentUserName})}
                </a>
              ) : (
                !isLoggedIn && (
                  <a
                    className="hidden sm:inline-flex text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors"
                    href={loginUrl}
                  >
                    {t('nav.cta.login')}
                  </a>
                )
              )}
              <a className="btn-primary text-sm font-semibold text-white px-5 py-2.5 rounded-md" href={loginUrl}>
                {isLoggedIn ? t('nav.cta.enterWorkspace') : t('nav.cta.freeTrial')}
              </a>
              <HamburgerButton onClick={toggleMobileMenu} />
            </div>
          </div>
        </div>
      </nav>
      {mobileMenuIsOpen && (
        <MobileMenuPanel
          closeMenu={closeMobileMenu}
          currentUserName={currentUserName}
          isLoggedIn={isLoggedIn}
          onLogin={() => {
            window.location.href = loginUrl;
          }}
        />
      )}
    </div>
  );
};

export default TopNav;
